import { MED_SKU, getEligibleMedProducts } from "src/utils/medEligibility";
import { hasWeightLossGoal } from "src/utils/userSegment/features";

export enum NON_MED_PLAN {
  NOOM_WEIGHT = "NOOM_WEIGHT",
  FREEMIUM = "FREEMIUM",
}

export type HealthPlan = MED_SKU | NON_MED_PLAN;

function isProductEligible(eligibleProducts: MED_SKU[], sku: MED_SKU): boolean {
  return eligibleProducts.includes(sku);
}

interface RecommendationContext {
  eligibleProducts: MED_SKU[];
  weightLossGoal: boolean;
}

interface PlanRecommendationRule {
  plan: HealthPlan;
  condition: (context: RecommendationContext) => boolean;
}

/**
 * Priority-ordered configuration for health plan recommendations.
 * First matching rule wins.
 */
const PLAN_RECOMMENDATION_CONFIG: PlanRecommendationRule[] = [
  // Full-dose GLP-1 with weight loss (priority: Compounded > Liraglutide > Microdose)
  {
    plan: MED_SKU.COMPOUNDED,
    condition: ({ weightLossGoal, eligibleProducts }) =>
      weightLossGoal && isProductEligible(eligibleProducts, MED_SKU.COMPOUNDED),
  },
  {
    plan: MED_SKU.GENERIC_LIRAGLUTIDE,
    condition: ({ weightLossGoal, eligibleProducts }) =>
      weightLossGoal &&
      isProductEligible(eligibleProducts, MED_SKU.GENERIC_LIRAGLUTIDE),
  },
  {
    plan: MED_SKU.COMPOUNDED_MICRODOSE,
    condition: ({ weightLossGoal, eligibleProducts }) =>
      weightLossGoal &&
      isProductEligible(eligibleProducts, MED_SKU.COMPOUNDED_MICRODOSE),
  },

  {
    plan: MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
    condition: ({ eligibleProducts }) =>
      isProductEligible(
        eligibleProducts,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
      ),
  },

  {
    plan: MED_SKU.BRANDED,
    condition: ({ weightLossGoal, eligibleProducts }) =>
      weightLossGoal && isProductEligible(eligibleProducts, MED_SKU.BRANDED),
  },

  {
    plan: MED_SKU.ORALS_METFORMIN,
    condition: ({ weightLossGoal, eligibleProducts }) =>
      weightLossGoal &&
      isProductEligible(eligibleProducts, MED_SKU.ORALS_METFORMIN),
  },

  {
    plan: NON_MED_PLAN.NOOM_WEIGHT,
    condition: ({ weightLossGoal }) => weightLossGoal,
  },
  {
    plan: NON_MED_PLAN.FREEMIUM,
    condition: () => true,
  },
];

export function getRecommendedHealthPlan(): HealthPlan | undefined {
  const eligibleProducts = getEligibleMedProducts();

  const context: RecommendationContext = {
    eligibleProducts,
    weightLossGoal: hasWeightLossGoal(),
  };

  for (const rule of PLAN_RECOMMENDATION_CONFIG) {
    if (rule.condition(context)) {
      return rule.plan;
    }
  }

  return undefined;
}
